import { Formik, FormikHelpers } from "formik";
import {
  useFormSubmit,
  ControlcenterMelcoModelsSupplier,
  UseApiQueryKey,
} from "melco-shared-logic";
import { QueryObserverResult, useQueryClient } from "react-query";
import { SubmitAction } from "../../header/SavePublishHeader";
//import { ALL_PUBLISHED_STATES } from "../../../helper/publishedState";
import { DirtyFormPrompt } from "../../form/DirtyFormPrompt";
import { useUpdateExistingSupplierDraft } from "../../../hooks/suppliers/useUpdateExistingSupplierDraft";
import { DisplaySupplierEditForm } from "./DisplaySupplierEditForm";
import { usePublishSupplier } from "../../../hooks/suppliers/usePublishSupplier";

type SupplierEditFormProps = {
  isSupplierDrawerOpen: boolean;
  closeDrawerAction: () => void;
  setSupplierDrawerOpen: (v: boolean) => void;
  supplier: ControlcenterMelcoModelsSupplier;
  refetch: () => Promise<
    QueryObserverResult<ControlcenterMelcoModelsSupplier, unknown>
  >;
  queryKey: UseApiQueryKey;
};

type SupplierEditFormValueType = {
  id: string;
  name: string;

  supplier_type: string;
  submitAction: SubmitAction;
  publish_state: number;
  created_date: Date;
  publish_date: Date;
};

export const supplierToInitialValues = (
  supplier: ControlcenterMelcoModelsSupplier
) => {
  const { id, name, supplier_type, publish_state, publish_date, created_date } =
    supplier;

  const initialValues: SupplierEditFormValueType = {
    id: id!,
    name: name ?? "",

    supplier_type: supplier_type ?? "",
    publish_state: publish_state,
    created_date: created_date!,
    publish_date: publish_date!,
    submitAction: "save",
  };

  return initialValues;
};

export const SupplierEditForm: React.FC<SupplierEditFormProps> = ({
  supplier,
  refetch,
  queryKey,
}) => {
  const queryClient = useQueryClient();

  const { id, created_date, publish_state, publish_date } = supplier;

  //const queryKey = ["supplier", id, ALL_PUBLISHED_STATES];
  const updateExistingDraft = useUpdateExistingSupplierDraft();
  const publishSupplier = usePublishSupplier();

  const onSubmit = async (
    values: SupplierEditFormValueType,
    formikHelpers: FormikHelpers<SupplierEditFormValueType>
  ) => {
    const { submitAction } = values;

    if (submitAction === "publish") {
      await onPublish(values, formikHelpers, { isManuallyResettingForm: true });
    } else {
      await onSave(values, formikHelpers, { isManuallyResettingForm: true });
    }
  };

  const handleSaveAndPublish = async (
    values: SupplierEditFormValueType,
    formikHelpers: FormikHelpers<SupplierEditFormValueType>,
    options: {
      publish: boolean;
    }
  ) => {
    const { publish } = options;
    const { name, supplier_type } = values;

    let newEntity = await updateExistingDraft({
      id: id,
      name: name,

      //  supplier_type: supplier_type
    });

    if (publish) {
      newEntity = await publishSupplier({
        id: id!,
      });
    }

    // update ui with new data
    formikHelpers.resetForm({
      values: supplierToInitialValues(
        newEntity ?? {
          id: id!,
          name: name ?? "",

          supplier_type: supplier_type ?? "",
          publish_state: publish_state,
          created_date: created_date!,
          publish_date: publish_date!,
        }
      ),
    });
    queryClient.setQueryData(queryKey, newEntity);

    return {
      successMessages: [
        {
          message: "default",
        },
      ],
    };
  };

  const formSubmitWithPublish = (publish: boolean) => {
    return [
      async (
        values: SupplierEditFormValueType,
        formikHelpers: FormikHelpers<SupplierEditFormValueType>
      ) => {
        return handleSaveAndPublish(values, formikHelpers, {
          publish,
        });
      },
      {
        translationPrefix: [
          "managesuppliers.actions",
          publish ? "publish" : "save",
        ].join("."),
        onError: () => {
          // refetch entity from server
          queryClient.invalidateQueries(queryKey);
        },
      },
    ] as const;
  };

  const onSave = useFormSubmit(...formSubmitWithPublish(false));
  const onPublish = useFormSubmit(...formSubmitWithPublish(true));

  const initialValues = supplierToInitialValues(supplier);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validateOnBlur={false}
      validateOnChange={false}
    >
      <DirtyFormPrompt>
        <DisplaySupplierEditForm supplier={supplier} refetch={refetch} />
      </DirtyFormPrompt>
    </Formik>
  );
};
